import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { Button, Form, Input } from 'antd';
import { AdditionFieldEnum, AddtionalList, ProviderChannelAdditional } from 'types/channelApplicationProvider';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { EMPTY_PLACEHOLDER } from 'constants/common';

interface IProps {
    formName: string;
    showEditButtons?: boolean;
    initFormValue: Partial<ProviderChannelAdditional>,
    enable: boolean;
    onEditStatusChange: (v: boolean, formName: string) => void;
    onUpdateService: (formName: string, formValue: ProviderChannelAdditional) => void;
}

const Membership = forwardRef(({
    initFormValue,
    formName,
    showEditButtons = true,
    enable,
    onUpdateService,
    onEditStatusChange,
}: IProps, ref) => {
    const [membershipForm] = Form.useForm<ProviderChannelAdditional>();

    useEffect(() => {
        membershipForm.setFieldsValue(initFormValue);
    }, [initFormValue, membershipForm]);

    const handleFormFinish = useCallback(async () => {
        membershipForm.validateFields().then((values: ProviderChannelAdditional) => {
            if (typeof onUpdateService === 'function') {
                onUpdateService(formName, {
                    ...initFormValue,
                    associationAdditionalList: values.associationAdditionalList ? values.associationAdditionalList?.filter((item) => item.name?.replace(/\s*/g, '')) : [],
                });
            }
        }).catch((e) => {
            console.error(e);
        });
        // onUpdateService(formName, formValue);
        // setSubmitData(formValue);
    }, [formName, initFormValue, onUpdateService, membershipForm]);

    const getFormValues = useCallback(async () => {
        let validRes = {};
        try {
            const resp = await membershipForm.validateFields();

            validRes.associationAdditionalList = resp.associationAdditionalList ? resp.associationAdditionalList?.filter((item) => item.name?.replace(/\s*/g, '')) : [];
        } catch (e) {
            validRes = e;
            console.error(e);
        }

        return validRes;
    }, [membershipForm]);

    useImperativeHandle(ref, () => ({
        getFromValues: getFormValues,
    }));

    const reviewData = useMemo(() => (initFormValue?.associationAdditionalList ? initFormValue.associationAdditionalList?.filter((item) => item.name?.replace(/\s*/g, '')) : []), [initFormValue]);

    const renderEditButtons = useMemo(() => {
        if (showEditButtons) {
            return !enable ? (
                <div key={`${formName}_disable`} className={s.editButtons}>
                    <Button
                        // size="small"
                        onClick={() => {
                            onEditStatusChange(true, formName);
                        }}
                    >
                        Edit
                    </Button>
                </div>
            ) : (
                <div key={`${formName}_enable`} className={s.editButtons}>
                    <Button
                        // size="small"
                        onClick={() => {
                            onEditStatusChange(false, formName);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        // size="small"
                        type="primary"
                        onClick={handleFormFinish}
                    >
                        Save changes
                    </Button>
                </div>
            );
        }
        return null;
    }, [enable, formName, onEditStatusChange, showEditButtons, handleFormFinish]);

    const renderAdditionalReview = useMemo(() => {
        return (
            <div className={s.row}>
                <div
                    className={s.displayItem}
                >
                    <div className={s.value}>
                        {
                            reviewData.length > 0 ?
                                <ul>
                                    {
                                        reviewData.map((item, index) => <li key={index}>{item.name}</li>)
                                    }
                                </ul> :
                                EMPTY_PLACEHOLDER
                        }
                    </div>
                </div>
            </div>
        );
    }, [reviewData]);

    const renderAssociationFormList = useMemo(() => {
        return (
            <Form
                form={membershipForm}
                name={formName}
                className={commonS.formStyle1}
                initialValues={initFormValue}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <Form.List name="associationAdditionalList">
                    {
                        (fields, { add, remove }) => {
                            return (
                                <>
                                    {
                                        fields.map(({ key, name, ...restField }, index) => {
                                            const label = `Association ${index + 1}`;
                                            return (
                                                <div key={index} className={s.additionalBox}>
                                                    <div className={s.additionalForm}>
                                                        <div className={s.formBody}>
                                                            <div className={s.row}>
                                                                <div
                                                                    className={s.displayItem}
                                                                >
                                                                    <Form.Item
                                                                        {...restField}
                                                                        label={label}
                                                                        name={[name, AdditionFieldEnum.NAME]}
                                                                    >
                                                                        <Input placeholder="Ex. Member of the American Psychiatric Nurses Association" />
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className={s.formFooter}>
                                                            <Button onClick={() => remove(index)}>Remove</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }
                                    <Form.Item>
                                        <div className={s.additionalTitle} onClick={() => add()}>+ Add another association</div>
                                    </Form.Item>
                                </>
                            );
                        }
                    }
                </Form.List>
            </Form>
        );
    }, [formName, initFormValue, membershipForm]);

    return (
        <div className={s.wrap}>
            <div className={`${s.module} ${s.moduleTitle}}`}>
                <h3>
                    <span>Professional memberships or associations</span>
                    <div className={s.buttonsWrap}>
                        {renderEditButtons}
                        {/* { enableReview && <Button className={s.showReviewModalButton} type="default" size="large" onClick={() => onShowReviewModal?.()}>Add reviews</Button>} */}
                    </div>
                </h3>
                {
                    !enable ? renderAdditionalReview : renderAssociationFormList
                }
            </div>
        </div>
    );
});

export default Membership;
